// Appointment times belong to the practice, not to the machine looking at them.
//
// 14:30 is 14:30 at the clinic whether the receptionist's laptop is set to
// Chicago, New York or — as happened — somewhere that rendered a 2:30 PM
// appointment as 9:30 AM. Every time in the calendar is a clinic wall-clock
// time, and this is where that is enforced.
//
// The database agrees: `startTime` is `timestamp without time zone`, holding
// clinic wall-clock, and the API converts it out using the server's own
// TZ=America/Chicago. This undoes that conversion on the way back in, so the
// browser's own zone never gets a say.

/** One place to change if the practice ever moves. */
export const PRACTICE_TIME_ZONE = 'America/Chicago';

export const PRACTICE_TIME_ZONES = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (no daylight time)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
] as const;

let activePracticeTimeZone = PRACTICE_TIME_ZONE;
const zoneListeners = new Set<() => void>();

export function getPracticeTimeZone(): string {
  return activePracticeTimeZone;
}

function isUsableTimeZone(zone: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function listPracticeTimeZones(): Array<{ value: string; label: string; group: 'common' | 'all' }> {
  const commonValues = new Set<string>(PRACTICE_TIME_ZONES.map((z) => z.value));
  let all: string[] = [];
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    all = supported ? supported.call(Intl, 'timeZone') : [];
  } catch {
    all = [];
  }
  const rest = all.filter((z) => !commonValues.has(z)).sort((a, b) => a.localeCompare(b));
  return [
    ...PRACTICE_TIME_ZONES.map((z) => ({ value: z.value, label: z.label, group: 'common' as const })),
    ...rest.map((z) => ({ value: z, label: z.replace(/_/g, ' '), group: 'all' as const })),
  ];
}

export function setPracticeTimeZone(zone: string | null | undefined): void {
  const next = zone && isUsableTimeZone(zone) ? zone : PRACTICE_TIME_ZONE;
  if (next === activePracticeTimeZone) return;
  activePracticeTimeZone = next;
  zoneListeners.forEach((fn) => fn());
}

export function subscribePracticeTimeZone(listener: () => void): () => void {
  zoneListeners.add(listener);
  return () => { zoneListeners.delete(listener); };
}


const pad = (n: number) => String(n).padStart(2, '0');

/**
 * An ISO instant → the clinic's wall-clock time as a **zone-less** string
 * (`YYYY-MM-DDTHH:mm:ss`).
 *
 * Zone-less is the point. Handed to a calendar or `new Date()`, a string with
 * no offset is treated as floating local time and placed exactly where it says,
 * so no second conversion can happen on top of the first.
 */
export function toPracticeWallClock(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) return typeof iso === 'string' ? iso : '';

  // en-CA gives YYYY-MM-DD; the parts are read individually so the format of
  // any one locale is never relied on.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: getPracticeTimeZone(),
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value || '00';
  // Midnight comes back as '24' in some engines.
  const hour = get('hour') === '24' ? '00' : get('hour');

  return `${get('year')}-${get('month')}-${get('day')}T${pad(Number(hour))}:${get('minute')}:${get('second')}`;
}

/** 'CST' or 'CDT', so a time on screen can say which clock it is on. */
export function practiceTimeLabel(iso?: string | Date | null): string {
  const date = iso ? (iso instanceof Date ? iso : new Date(iso)) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const part = new Intl.DateTimeFormat('en-US', {
    timeZone: getPracticeTimeZone(),
    timeZoneName: 'short',
  }).formatToParts(date).find((p) => p.type === 'timeZoneName');
  return part?.value || '';
}

/** True when the viewer's machine disagrees with the practice clock. */
export function viewerIsElsewhere(): boolean {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone !== getPracticeTimeZone();
  } catch {
    return false;
  }
}

/**
 * An instant → the way a person says it: "Tuesday 8 September at 9:00 AM",
 * always on the clinic clock.
 *
 * For sentences a patient reads. Assembled from parts rather than by slicing up
 * another formatted string — a regex over "Joel Schwartz on 09/08/2026" left the
 * patient's own name in the middle of their cancellation message.
 */
export function practiceDateTimeLabel(iso?: string | Date | null): string {
  if (!iso) return '';
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: getPracticeTimeZone(),
    weekday: 'long', day: 'numeric', month: 'long',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  const period = (get('dayPeriod') || '').toUpperCase();

  return `${get('weekday')} ${get('day')} ${get('month')} at ${get('hour')}:${get('minute')} ${period}`;
}
